import { z } from 'zod';
import { documentStatusSchema, documentTypeSchema, uuidSchema } from './common.js';
/**
 * Ventes, encaissements et sessions de caisse (Sprint 5).
 *
 * ⭐ Une seule forme pour devis, factures, acomptes et avoirs : le discriminant
 * est `docType`. Les quatre partagent 95 % de leur structure, et les séparer
 * aurait quadruplé le code de lecture pour une différence tenant en une colonne.
 *
 * ⚠️ Aucun TOTAL n'est accepté depuis le client. Ils sont recalculés par
 * `computeDocument` à l'écriture : un montant venu du navigateur est une donnée
 * que l'utilisateur peut choisir, et une remise de 100 % s'obtiendrait avec un
 * outil de développement. Le client les calcule aussi, mais pour AFFICHER.
 */
export const PAYMENT_METHODS = ['cash', 'card', 'transfer', 'check', 'other'];
export const PAYMENT_METHOD_LABELS = {
    cash: 'Espèces',
    card: 'Carte',
    transfer: 'Virement',
    check: 'Chèque',
    other: 'Autre',
};
// ⭐ `documentTypeSchema` et `documentStatusSchema` viennent de `common.ts` :
// les redéfinir ici aurait donné deux vérités sur les mêmes valeurs.
export const paymentMethodSchema = z.enum(PAYMENT_METHODS);
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));
export const transactionLineInputSchema = z.object({
    id: uuidSchema.optional(),
    /** Rattachement à un article du catalogue — facultatif : une ligne libre est permise. */
    itemId: uuidSchema.optional().or(z.literal('').transform(() => undefined)),
    /**
     * ⚠️ COPIÉ depuis l'article au moment de la vente, jamais lu par jointure.
     * Renommer un produit ne doit pas réécrire l'historique des ventes.
     */
    label: z.string().trim().min(1, 'Libellé obligatoire').max(200),
    description: optionalText(1000),
    quantity: z.coerce
        .number()
        .positive('La quantité doit être positive')
        .max(1_000_000, 'Quantité invraisemblable'),
    /** Prix unitaire HT, entier dans la plus petite unité de la devise. */
    unitPrice: z.coerce.number().int('Montant en plus petite unité').min(0),
    discountRate: z.coerce.number().min(0).max(100).default(0),
    taxRate: z.coerce.number().min(0).max(100).default(0),
});
export const transactionInputSchema = z.object({
    id: uuidSchema.optional(),
    docType: documentTypeSchema.default('invoice'),
    /** NULL = vente comptoir anonyme, cas courant en boutique. */
    customerId: uuidSchema.optional().or(z.literal('').transform(() => undefined)),
    issueDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date attendue au format AAAA-MM-JJ')
        .optional(),
    dueDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date attendue au format AAAA-MM-JJ')
        .optional()
        .or(z.literal('').transform(() => undefined)),
    currencyCode: z.string().length(3).toUpperCase().optional(),
    /**
     * Remise globale en POURCENTAGE, appliquée après les remises de ligne.
     *
     * ⚠️ Un pourcentage et non un montant, bien que la maquette de caisse montre
     * des francs : c'est la forme qu'attend `computeDocument`, seul calcul
     * autorisé. Saisir un montant obligerait à le reconvertir en taux, et
     * l'arrondi ferait diverger l'écran du serveur — exactement ce que le
     * paquet partagé existe pour empêcher.
     */
    globalDiscountRate: z.coerce.number().min(0).max(100).default(0),
    notes: optionalText(2000),
    terms: optionalText(2000),
    /** Référence provisoire d'une vente encaissée sans réseau (`L-2026-0042`). */
    localRef: optionalText(40),
    parentId: uuidSchema.optional(),
    sessionId: uuidSchema.optional(),
    lines: z.array(transactionLineInputSchema).min(1, 'Au moins une ligne'),
});
/**
 * Modification d'un document existant.
 *
 * ⚠️ Le MOTIF n'est pas décoratif : il est exigé dès que le document n'est
 * plus un brouillon (voir `isMutableParAdmin`). Une facture validée qu'on
 * corrige sans dire pourquoi est une facture qu'on ne saura pas défendre —
 * ni devant le client, ni devant un contrôle.
 */
export const transactionUpdateSchema = transactionInputSchema.extend({
    motif: optionalText(300),
});
/**
 * Vente encaissée HORS LIGNE, poussée en une seule mutation.
 *
 * ⭐ Tout voyage ensemble : l'entête, les lignes et les règlements. Trois
 * mutations séparées pourraient arriver dans le désordre, ou l'une échouer, et
 * laisser une vente validée mais non encaissée sur le serveur — l'incohérence
 * la plus pénible à démêler pour un commerçant.
 *
 * ⚠️ L'appareil ne peut PAS attribuer de numéro légal : il faudrait un verrou
 * sur la séquence, donc le serveur. Le ticket porte une référence provisoire
 * « L-… » jusqu'à la synchronisation, où le numéro définitif est attribué.
 */
export const venteSyncSchema = transactionInputSchema.extend({
    /** Vrai pour une vente de caisse : le serveur validera et numérotera. */
    validated: z.boolean().default(false),
    paiements: z.array(z.object({
        id: uuidSchema.optional(),
        method: paymentMethodSchema,
        amount: z.coerce.number().int(),
        // Voir `paymentInputSchema` : la caisse hors ligne les pousse aussi.
        tendered: z.coerce.number().int().min(0).optional(),
        change: z.coerce.number().int().min(0).optional(),
        reference: z.string().trim().max(80).optional(),
        sessionId: uuidSchema.optional(),
    })).default([]),
});
export const paymentInputSchema = z.object({
    id: uuidSchema.optional(),
    method: paymentMethodSchema,
    /**
     * Ce que le client a tendu, et ce qu'on lui a rendu.
     *
     * ⚠️ Facultatifs, et ils le restent : les imposer casserait toute écriture
     * venue d'un appareil resté sur une version antérieure, dont les ventes
     * hors ligne remontent parfois des jours plus tard.
     */
    tendered: z.coerce.number().int().min(0).optional(),
    change: z.coerce.number().int().min(0).optional(),
    /**
     * ⚠️ Peut être NÉGATIF : c'est ainsi qu'on corrige un encaissement, la table
     * étant en ajout seul. Zéro, en revanche, n'a aucun sens.
     */
    amount: z.coerce.number().int('Montant en plus petite unité').refine((v) => v !== 0, 'Montant nul'),
    reference: optionalText(80),
    notes: optionalText(500),
    sessionId: uuidSchema.optional(),
});
export const cashSessionOpenSchema = z.object({
    /** Fond de caisse au moment de l'ouverture. */
    openingFloat: z.coerce.number().int().min(0).default(0),
    notes: optionalText(500),
});
export const cashSessionCloseSchema = z.object({
    /** Ce que le caissier a réellement compté dans le tiroir. */
    closingCounted: z.coerce.number().int().min(0),
    notes: optionalText(500),
});
export const transactionQuerySchema = z.object({
    q: z.string().trim().max(120).optional(),
    docType: documentTypeSchema.optional(),
    status: documentStatusSchema.optional(),
    customerId: uuidSchema.optional(),
    sort: z.enum(['issueDate', '-issueDate', 'total', '-total']).default('-issueDate'),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
export const cashMovementInputSchema = z.object({
    /**
     * ⚠️ Signé, et jamais nul. Le signe porte le SENS du mouvement : un champ
     * séparé « apport / prélèvement » doublerait l'information et laisserait la
     * porte ouverte à un prélèvement de montant positif.
     */
    amount: z.coerce.number().int().refine((v) => v !== 0, 'Montant nul'),
    /**
     * ⚠️ Obligatoire. « Prélèvement de 200 000 » sans raison est exactement ce
     * qu'un contrôle de caisse cherche à éclaircir.
     */
    reason: z.string().trim().min(2, 'Motif attendu').max(120),
    notes: optionalText(500),
});
/**
 * Ce que le tiroir DEVRAIT contenir à la clôture.
 *
 * ⭐ Une seule définition, parce qu'elle est lue à trois endroits — l'écran de
 * clôture, le ticket de clôture et le serveur qui fige l'écart. Trois copies
 * auraient divergé, et un écart calculé différemment à l'écran et sur le
 * papier est pire que pas d'écart du tout.
 *
 * ⚠️ Les espèces SEULEMENT. Une carte ne passe pas par le tiroir : la compter
 * ferait apparaître un manque égal au montant encaissé par terminal.
 */
export function attenduEnCaisse(session) {
    return session.openingFloat + (session.cashTotal ?? 0) + (session.movementsTotal ?? 0);
}
/**
 * Référence affichable d'un document.
 *
 * ⭐ Le numéro légal s'il existe, sinon la référence provisoire. Une vente
 * encaissée hors ligne doit pouvoir être nommée au client AVANT d'avoir
 * rejoint le serveur — « votre ticket L-2026-0042 » vaut mieux que rien.
 */
export function referenceAffichable(t) {
    return t.number ?? t.localRef ?? '—';
}
/**
 * Ce document pèse-t-il dans le CHIFFRE D'AFFAIRES ?
 *
 * ⭐ Une seule définition, parce qu'elle est recopiée partout où l'on additionne
 * de l'argent : tableau de bord, totaux du jour, cumul d'achats d'un client,
 * fiche client. Quatre endroits, donc quatre occasions d'oublier un cas.
 *
 * ⚠️ Devis et commandes sont exclus par leur TYPE, pas par leur statut. Tant
 * que les devis restaient brouillons, le filtre « pas un brouillon » suffisait
 * par accident. Depuis qu'ils portent un numéro — donc le statut « validé » —,
 * ce filtre les laisserait passer : une proposition de prix jamais acceptée
 * gonflerait le chiffre d'affaires du commerçant.
 *
 * ⚠️ Cette fonction dit SI un document compte, `signeDansLeChiffre` dit dans
 * quel sens. Additionner sans le signe fait grossir le chiffre à chaque avoir.
 */
export function compteDansLeChiffre(t) {
    return signeDansLeChiffre(t) !== 0;
}
/**
 * Ce document est-il un ENGAGEMENT — devis ou commande — plutôt qu'une
 * facture ?
 *
 * ⭐ Un engagement ne se règle jamais directement : rien n'est dû tant qu'il
 * n'a pas été transformé en facture, c'est ELLE qui s'encaisse. « Réglé »,
 * « Reste dû » et l'acompte n'ont donc pas de sens ici — les afficher
 * transformerait une proposition ou une promesse en créance.
 *
 * ⚠️ Trois écrans recopiaient `docType !== 'quote'` sans jamais exclure la
 * commande : elle affichait un « Reste dû » que personne ne devait, pour la
 * même raison qui avait déjà été corrigée pour le devis.
 */
export function estEngagement(docType) {
    return docType === 'quote' || docType === 'order';
}
/**
 * Le refus d'annuler une commande déjà facturée — le MÊME texte au serveur et
 * dans le navigateur.
 *
 * ⭐ Le navigateur le connaît avant le serveur : une facture faite en caisse
 * attend dans la file de synchronisation, mais elle est déjà dans la réplique.
 * C'est pour cela que l'écran refuse lui-même, sans attendre la réponse d'un
 * serveur qui ne sait pas encore.
 */
export const REFUS_ANNULER_COMMANDE_FACTUREE = 'Cette commande a déjà été transformée en facture : annulez la facture plutôt que la commande.';
/**
 * Dans quel SENS ce document pèse-t-il sur le chiffre d'affaires ?
 *
 * ⭐ +1 pour une vente, −1 pour un AVOIR, 0 pour ce qui n'engage rien.
 *
 * ⚠️ Un avoir porte un montant POSITIF, comme tout document : c'est son type
 * qui dit qu'il rend de l'argent, pas son signe. Additionné sans précaution, il
 * gonflait le chiffre d'affaires du montant qu'il était censé lui retirer —
 * l'erreur compte donc double, et elle grandit à chaque geste commercial.
 *
 * ⚠️ Le DEVIS et la COMMANDE valent zéro : ce sont des engagements, pas des
 * recettes. Le chiffre d'affaires se reconnaît à la facture.
 */
export function signeDansLeChiffre(t) {
    if (t.status === 'draft' || t.status === 'cancelled')
        return 0;
    if (t.docType === 'quote' || t.docType === 'order')
        return 0;
    return t.docType === 'credit_note' ? -1 : 1;
}
export function resultatDesVentes(ventes, articles) {
    let ventesHt = 0;
    let coutAchat = 0;
    let lignesSansPrixAchat = 0;
    for (const v of ventes) {
        // La MÊME règle que le chiffre d'affaires : un devis, une commande ou un
        // brouillon n'ont rien vendu, un avoir rembourse.
        const signe = signeDansLeChiffre(v);
        if (signe === 0)
            continue;
        ventesHt += signe * (v.totalAmount - v.taxAmount);
        for (const ligne of v.lines) {
            const article = ligne.itemId ? articles.get(ligne.itemId) : undefined;
            if (!article || (article.kind === 'product' && article.purchasePrice <= 0)) {
                lignesSansPrixAchat += 1;
                continue;
            }
            // Quantité décimale possible (1,5 kg) : l'arrondi se fait par ligne,
            // les montants restant des entiers (ADR-10).
            coutAchat += signe * Math.round(ligne.quantity * article.purchasePrice);
        }
    }
    return { ventesHt, coutAchat, lignesSansPrixAchat };
}
/**
 * Libellé du statut, qui DÉPEND DU TYPE de document.
 *
 * ⚠️ « validé » ne veut pas dire la même chose sur une facture et sur un devis.
 * Sur une facture, il reste à encaisser — « À régler ». Sur un devis, il n'y a
 * rien à régler : il a été émis, et on attend la réponse du client. Le libellé
 * commun affichait « À régler » sur des devis que personne n'avait acceptés.
 */
export function libelleStatut(docType, status) {
    if (docType === 'quote') {
        return { draft: 'Brouillon', validated: 'Émis', cancelled: 'Refusé' }[status] ?? 'Émis';
    }
    if (docType === 'order') {
        // Une commande est passée, pas « à régler » : elle le deviendra en facture.
        return { draft: 'Brouillon', validated: 'En cours', cancelled: 'Annulée' }[status] ?? 'En cours';
    }
    return {
        draft: 'Brouillon',
        validated: 'À régler',
        partially_paid: 'Partiellement réglée',
        paid: 'Réglée',
        cancelled: 'Annulée',
    }[status];
}
/** Reste dû, jamais négatif : un trop-perçu ne se présente pas comme une dette. */
export function resteDu(t) {
    return Math.max(0, t.totalAmount - t.paidAmount);
}
/**
 * Le solde d'un document, SIGNÉ — qui doit quoi à qui.
 *
 * ⭐ Positif : le client doit encore de l'argent à la boutique.
 * ⭐ Négatif : la boutique doit de l'argent au client.
 *
 * ⚠️ `resteDu` écrase le second cas à zéro, et c'est voulu là où il sert —
 * une facture ne réclame pas une somme négative. Mais le cas EXISTE et il
 * fallait un chiffre pour le dire : au comptoir, une caisse sans monnaie
 * encaisse le billet entier et reste devoir la différence. Sans ce solde, la
 * dette n'apparaissait nulle part et se réglait de mémoire.
 */
export function soldeDocument(t) {
    return t.totalAmount - t.paidAmount;
}
/**
 * « 27/08/2026 14:32 ».
 *
 * ⭐ Une vente se repère à l'HEURE, pas au jour : un commerçant en fait
 * trente dans la même journée, et la date seule ne distingue rien. C'est
 * `createdAt` qu'on lit, pas `issueDate` — cette dernière n'a pas d'heure.
 */
export function dateHeureLisible(iso) {
    if (!iso)
        return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return '—';
    return d.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}
//# sourceMappingURL=sales.js.map